import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import { Box, Typography } from '@mui/material';
import moment from 'moment';

import { FBoxContainer, FProgressBar, FHSelect } from '../components/base';
import { sessionActions, serviceCodeActions, timesheetActions, clientActions } from '../store';
import useReadOnlyTimesheetView from '../hooks/useReadOnlyTimesheetView';
import { getUtcOffset } from '../utils/common';
import TimesheetTable from '../components/layout/timesheets/TimesheetTable';
import TimesheetHeading from '../components/layout/timesheets/TimesheetHeading';
import PageContainer from '../components/layout/PageContainer';
import { BackgroundTypes, Roles } from '../constants';
import { areUserRolesIn } from '../helpers';

import type { IFilterAndSortOption, SelectValueType } from '../utils/types';
import type { RootState } from '../store';

const Timesheet = () => {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const {
    payPeriods,
    selectedPayPeriod = [],
    loading: timesheetsLoading,
    isPayPeriodEditable,
  } = useSelector((state: RootState) => state.timesheets);
  const { loading: sessionsLoading, list: sessions = [] } = useSelector(
    (state: RootState) => state.sessions,
  );
  const { loading: utilizationLoading } = useSelector((state: RootState) => state.utilizations);
  const { user } = useSelector((state: RootState) => state.auth);
  const isReadOnlyView = useReadOnlyTimesheetView();
  const doNotReadNonTherapyHours =
    areUserRolesIn(user?.roles, [Roles.Guardian]) && !areUserRolesIn(user?.roles, [Roles.Vbt]);

  const loadSessionsByDefault = areUserRolesIn(user?.roles, [Roles.Guardian, Roles.Prbt]);

  const [payPeriodList, setPayPeriodList] = useState<Array<Array<string>>>([]);
  const [selectedSessionFilter, setSelectedSessionFilter] = useState<IFilterAndSortOption | null>();

  useEffect(() => {
    const today = moment(new Date());
    const startDate = moment().subtract(3, 'months');
    const serviceCodeParams = { is_timesheet_usable: true };
    dispatch(sessionActions.updateState({ key: 'list', data: [] }));
    dispatch(timesheetActions.updateState({ key: 'list', value: [] }));
    dispatch(timesheetActions.updateState({ key: 'selectedPayPeriod', value: [] }));
    dispatch(
      timesheetActions.getPayPeriods({
        params: {
          start: startDate.toISOString(),
          end: today.toISOString(),
          utc_offset: getUtcOffset(moment(new Date())),
        },
        getAccessTokenSilently,
      }),
    );
    dispatch(clientActions.getList({ getAccessTokenSilently }));
    dispatch(serviceCodeActions.getList({ getAccessTokenSilently, params: serviceCodeParams }));

    return () => {
      dispatch(sessionActions.updateState({ key: 'list', data: [] }));
      dispatch(timesheetActions.updateState({ key: 'list', value: [] }));
      dispatch(timesheetActions.updateState({ key: 'selectedPayPeriod', value: [] }));
      dispatch(timesheetActions.updateState({ key: 'removeNewTimesheet', value: true }));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedPayPeriod.length === 0) return;

    // get data for Non-therapy hours
    if (!doNotReadNonTherapyHours) {
      dispatch(
        timesheetActions.getList({
          params: {
            start: moment(selectedPayPeriod[0]).startOf('day').toISOString(),
            end: moment(selectedPayPeriod[1]).endOf('day').toISOString(),
            utc_offset: getUtcOffset(moment(new Date())),
          },
          getAccessTokenSilently,
        }),
      );
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPayPeriod]);

  useEffect(() => {
    if (selectedPayPeriod.length === 0 || sessions.length) return;

    // get data for Therapy hours
    if (loadSessionsByDefault || selectedSessionFilter) {
      dispatch(
        sessionActions.getList({
          params: {
            startDate: moment(selectedPayPeriod[0]).startOf('day'),
            endDate: moment(selectedPayPeriod[1]).endOf('day'),
            include_participated_only: !isReadOnlyView,
            find_by_provider_id_only: !isReadOnlyView, // Show sessions done by provider even if a client isn't assigned to it.
          },
          getAccessTokenSilently,
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPayPeriod, loadSessionsByDefault, selectedSessionFilter?.id]);

  useEffect(() => {
    if (!payPeriods) return;

    setPayPeriodList(
      [
        ...payPeriods.future.slice().reverse(),
        payPeriods.present,
        ...payPeriods.past.slice().reverse(),
      ].slice(0, 6) || [],
    );
    dispatch(timesheetActions.updateState({ key: 'selectedPayPeriod', value: payPeriods.present }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payPeriods]);

  const selectedPeriodIndex = useMemo(() => {
    if (!payPeriodList || !selectedPayPeriod) return -1;

    return payPeriodList.findIndex(
      (payPeriod) => payPeriod[0] === selectedPayPeriod[0] && payPeriod[1] === selectedPayPeriod[1],
    );
  }, [payPeriodList, selectedPayPeriod]);

  const handleUpdatePayPeriod = (value: SelectValueType) => {
    const index = Number(value);
    if (!Number.isInteger(index) || index < 0 || index >= payPeriodList.length) return;

    dispatch(
      timesheetActions.updateState({ key: 'selectedPayPeriod', value: payPeriodList[index] }),
    );
    dispatch(timesheetActions.updateState({ key: 'removeNewTimesheet', value: true }));
    dispatch(timesheetActions.updateState({ key: 'list', value: [] }));
    dispatch(sessionActions.updateState({ key: 'list', data: [] }));
  };

  const handleSessionFilterChange = (sessionFilter: IFilterAndSortOption | null) => {
    setSelectedSessionFilter(sessionFilter);
  };

  return (
    <PageContainer>
      {(sessionsLoading || timesheetsLoading || utilizationLoading) && <FProgressBar />}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'start', mt: 2 }}>
        <Typography fontWeight="bold" mb={1}>
          View pay period:
        </Typography>
        {payPeriodList?.length && payPeriodList[0].length > 0 && selectedPayPeriod?.length > 0 && (
          <FHSelect
            data-testid="payPeriodDropDown"
            inputHeight={40}
            sx={{ boxShadow: 'none' }}
            backgroundType={BackgroundTypes.Colored}
            options={payPeriodList.map((period, index) => ({
              value: index,
              label: `${moment(period[0]).format('MM/DD/YYYY')} - ${moment(period[1]).format(
                'MM/DD/YYYY',
              )}`,
            }))}
            optionDataTestIdPrefix="payPeriodItems"
            value={selectedPeriodIndex === -1 ? '' : selectedPeriodIndex}
            onUpdate={handleUpdatePayPeriod}
          />
        )}
      </Box>
      <FBoxContainer sx={{ mt: 3, mb: 10, flex: 0, boxShadow: 'none' }}>
        {!isReadOnlyView && isPayPeriodEditable && <TimesheetHeading />}
        {payPeriodList.length > 0 && (
          <TimesheetTable
            isReadOnlyView={isReadOnlyView}
            selectedPayPeriod={selectedPayPeriod}
            loadSessionsByDefault={loadSessionsByDefault}
            onSessionFilterChange={handleSessionFilterChange}
          />
        )}
      </FBoxContainer>
    </PageContainer>
  );
};

export default Timesheet;
